import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  publicSecondaryButtonSx,
  PUBLIC_BTN_RADIUS,
} from '../theme/publicPrimaryButtonSx';
import {
  publicBackdropGradientSx,
  publicHeaderTokens,
  publicMarketingTokens as tokens,
} from '../theme/publicMarketingStyles';
import smarthubLogo from '../assets/smarthub_logo_header2.png';
import heroAtmosphere from '../assets/smarthubbg_launch.png';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

const FONT = tokens.fontFamily;
const CONTACT_EMAIL = 'contact@prodactivesmarthub.com';

const fadeRise = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const softDrift = keyframes`
  0% { transform: scale(1.03) translate3d(0, 0, 0); }
  50% { transform: scale(1.07) translate3d(-0.8%, 0.6%, 0); }
  100% { transform: scale(1.03) translate3d(0, 0, 0); }
`;

const lineDraw = keyframes`
  from { transform: scaleX(0); opacity: 0; }
  to { transform: scaleX(1); opacity: 1; }
`;

const ambientPulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.55; }
`;

/**
 * Public shutdown notice for getsmarthub.co.
 * Centered one-viewport composition using SmartHub public marketing style.
 */
export default function ShutdownLandingPage() {
  const [ready, setReady] = useState(false);

  usePageMeta({
    title: "We're sorry that Smarthub is currently unavailable",
    description:
      "We're sorry that Smarthub is currently unavailable. Any questions, get in touch contact@prodactivesmarthub.com",
    canonical: 'https://getsmarthub.co/',
    ogUrl: 'https://getsmarthub.co/',
    ogImage: 'https://getsmarthub.co/og-image.png',
    ogImageAlt: "We're sorry that Smarthub is currently unavailable — by Prodactive Labs",
  });

  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots?.getAttribute('content');
    if (robots) robots.setAttribute('content', 'index, follow');

    const t = window.setTimeout(() => setReady(true), 40);
    return () => {
      window.clearTimeout(t);
      if (robots && prevRobots != null) {
        robots.setAttribute('content', prevRobots);
      }
    };
  }, []);

  const enter = (delay) =>
    ready
      ? `${fadeRise} 0.75s ${delay}s ease-out both`
      : 'none';

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        color: tokens.color.text,
        ...publicBackdropGradientSx,
      }}
    >
      {/* Existing launch artwork under the brand gradient */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${heroAtmosphere})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.22,
          animation: ready ? `${softDrift} 30s ease-in-out infinite` : 'none',
          willChange: 'transform',
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          ...publicBackdropGradientSx,
          opacity: 0.92,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: '50%',
          top: '42%',
          width: { xs: 280, md: 420 },
          height: { xs: 280, md: 420 },
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${publicHeaderTokens.accentSoft} 0%, transparent 68%)`,
          animation: ready ? `${ambientPulse} 7s ease-in-out infinite` : 'none',
          pointerEvents: 'none',
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: { xs: 3, sm: 4, md: 6 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 640,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={smarthubLogo}
            alt="Smarthub"
            draggable={false}
            sx={{
              height: { xs: 44, sm: 52, md: 60 },
              width: 'auto',
              mb: { xs: 3, md: 3.5 },
              userSelect: 'none',
              opacity: ready ? 1 : 0,
              animation: enter(0),
            }}
          />

          <Box
            aria-hidden
            sx={{
              width: { xs: 56, md: 72 },
              height: 2,
              mb: { xs: 2.75, md: 3.25 },
              borderRadius: 1,
              backgroundColor: publicHeaderTokens.accent,
              transformOrigin: 'center',
              animation: ready
                ? `${lineDraw} 0.65s 0.12s ease-out both`
                : 'none',
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                opacity: 1,
                transform: 'none',
              },
            }}
          />

          <Typography
            component="h1"
            sx={{
              m: 0,
              mb: { xs: 1.75, md: 2 },
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: {
                xs: 'clamp(1.85rem, 7vw, 2.4rem)',
                sm: '2.6rem',
                md: '3rem',
              },
              letterSpacing: '-0.035em',
              lineHeight: 1.1,
              color: tokens.color.text,
              textWrap: 'balance',
              opacity: ready ? 1 : 0,
              animation: enter(0.1),
            }}
          >
            We're sorry that Smarthub is currently unavailable
          </Typography>

          <Typography
            sx={{
              m: 0,
              mb: { xs: 3.5, md: 4.25 },
              maxWidth: 520,
              fontFamily: FONT,
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.6,
              color: tokens.color.textMuted,
              opacity: ready ? 1 : 0,
              animation: enter(0.2),
            }}
          >
            Any questions, get in touch{' '}
            <Box
              component="a"
              href={`mailto:${CONTACT_EMAIL}`}
              sx={{
                color: publicHeaderTokens.accent,
                textDecoration: 'underline',
                textUnderlineOffset: '0.18em',
                '&:hover': { color: tokens.color.text },
              }}
            >
              {CONTACT_EMAIL}
            </Box>
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              opacity: ready ? 1 : 0,
              animation: enter(0.3),
            }}
          >
            <Button
              component="a"
              href={`mailto:${CONTACT_EMAIL}`}
              variant="outlined"
              sx={{
                ...publicSecondaryButtonSx,
                px: { xs: 4, sm: 5 },
                py: 1.3,
                minWidth: { xs: 220, sm: 240 },
                borderRadius: PUBLIC_BTN_RADIUS,
                fontFamily: FONT,
                fontWeight: 600,
                fontSize: { xs: '0.9rem', md: '0.95rem' },
              }}
            >
              Contact us
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
